import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function JoinGroup() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${code}`);
        setPreview(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Invalid invite");
      }
    })();
  }, [code]);

  const accept = async () => {
    if (!user) {
      localStorage.setItem("stb_pending_invite", code);
      nav("/signup");
      return;
    }
    setJoining(true);
    try {
      const { data } = await api.post(`/invite/${code}/accept`);
      if (data.already_member) {
        toast.info("You're already in this group");
      } else {
        toast.success(`Joined ${preview.name}`);
      }
      nav(`/groups/${data.group_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to join");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-border border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(0,255,102,0.15), transparent 70%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-heading font-bold text-lg mb-10" data-testid="join-logo">
          <span className="h-5 w-5 rounded-md brand-bg" />
          Split the <span className="brand-text">____</span>
        </Link>

        {error ? (
          <div className="rounded-2xl border border-border bg-card/40 p-10 text-center" data-testid="invite-error">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="font-heading text-3xl tracking-tight mb-2">Invalid invite</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline" data-testid="invite-error-home">Back home</Button>
            </Link>
          </div>
        ) : !preview ? (
          <div className="rounded-2xl border border-border bg-card/40 p-10 text-center">
            <div className="h-8 w-8 mx-auto rounded-full border-2 border-border border-t-[var(--brand)] animate-spin" />
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card/40 p-8 sm:p-10 tracing-beam" data-testid="invite-preview">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-5">You've been invited</div>
            <div className="flex items-center gap-4 mb-7">
              <div className="h-16 w-16 rounded-2xl bg-foreground/5 border border-border flex items-center justify-center text-3xl shrink-0">
                {preview.emoji}
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-3xl tracking-tight truncate" data-testid="invite-group-name">{preview.name}</h1>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  {preview.group_type} · {preview.currency}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground mb-8 pb-6 border-b border-border">
              <span>From <span className="text-foreground">{preview.created_by_name}</span></span>
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {preview.members_preview.map((m, idx) => (
                    <div key={`${m.name || "m"}-${idx}`} className="h-7 w-7 rounded-full bg-foreground/10 border-2 border-card flex items-center justify-center text-xs">
                      {m.avatar_emoji}
                    </div>
                  ))}
                </div>
                <span className="font-mono text-xs">{preview.member_count} member{preview.member_count === 1 ? "" : "s"}</span>
              </div>
            </div>

            <Button
              onClick={accept} disabled={joining}
              className="w-full h-12 brand-bg text-black font-semibold hover:opacity-90 active:scale-[0.98] transition"
              data-testid="invite-accept-btn"
            >
              {joining ? "Joining…" : user ? (
                <>Join {preview.name} <Check className="ml-1.5 h-4 w-4" strokeWidth={2} /></>
              ) : (
                <>Create account to join <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} /></>
              )}
            </Button>
            {!user && (
              <p className="text-xs text-muted-foreground text-center mt-4">
                Already with us?{" "}
                <Link to="/login" className="brand-text hover:underline" data-testid="invite-login-link">Sign in</Link>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
